import React from "react";
import { Link } from "react-router-dom";
import { Calendar, Package, Plus, Share2, X, Sparkles, FileText } from "lucide-react";

/**
 * BookingsList — renders the customer's bookings with expandable details
 * and per-booking action buttons (Add-ons, Edit, Smart Invite, Text Invite, Cancel).
 * Pure presentational — all handlers come in as props.
 */
export default function BookingsList({
  allBookings,
  expandedBookingId,
  setExpandedBookingId,
  onAddAddon,
  onEditRequest,
  onSmartInvite,
  onTextInvite,
  onCancel,
}) {
  return (
    <div className="bg-white/95 backdrop-blur-sm rounded-2xl shadow-sm border border-[#f0e6d9] p-5" data-testid="customer-bookings">
      <h3 className="text-sm font-semibold text-[#1E1B4B] uppercase tracking-wider mb-3 flex items-center gap-2">
        <Calendar size={16} /> My Bookings
      </h3>
      {allBookings.length > 0 ? (
        <div className="space-y-3">
          {allBookings.map(b => (
            <BookingRow
              key={b.id}
              booking={b}
              expanded={expandedBookingId === b.id}
              onToggle={() => setExpandedBookingId(expandedBookingId === b.id ? null : b.id)}
              onAddAddon={() => onAddAddon(b)}
              onEditRequest={() => onEditRequest(b)}
              onSmartInvite={() => onSmartInvite(b)}
              onTextInvite={() => onTextInvite(b)}
              onCancel={() => onCancel(b)}
            />
          ))}
        </div>
      ) : (
        <div className="text-center py-8 text-[#475569]">
          <Package size={36} className="mx-auto mb-2 opacity-30" />
          <p className="text-sm">
            No bookings yet.{" "}
            <Link to="/#book-online" className="text-[#E83E8C] hover:underline font-medium">
              Book your first party!
            </Link>
          </p>
        </div>
      )}
    </div>
  );
}

function BookingRow({ booking: b, expanded, onToggle, onAddAddon, onEditRequest, onSmartInvite, onTextInvite, onCancel }) {
  const isCancelled = b.status === 'cancelled';
  const isCompleted = b.booking_status === 'completed';
  const isPast = new Date(b.date) < new Date(new Date().toDateString());
  const isUpcoming = !isCancelled && !isCompleted && !isPast;
  const statusLabel = isCancelled ? 'Cancelled' : (isCompleted || isPast) ? 'Completed' : 'Upcoming';
  const statusClass = isCancelled
    ? 'bg-gray-100 text-gray-500'
    : (isCompleted || isPast) ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700';

  const balance = b.total_amount && b.amount_paid && Number(b.total_amount) > Number(b.amount_paid)
    ? Number(b.total_amount) - Number(b.amount_paid)
    : null;

  return (
    <div
      className={`p-4 rounded-xl border text-sm transition-all ${isCancelled ? 'bg-gray-50 border-gray-200 opacity-50' : 'bg-[#FFFAF5] border-[#f0e6d9]'}`}
      data-testid={`booking-card-${b.id}`}
    >
      <div className="flex items-start justify-between gap-2 cursor-pointer" onClick={onToggle}>
        <div>
          <span className="font-semibold text-[#1E1B4B]">
            {new Date(b.date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
          </span>
          <span className="mx-2 text-[#E83E8C] font-medium">{b.package}</span>
          <span className="text-[#475569]">{b.occasion}</span>
          <p className="text-xs text-[#94a3b8] mt-0.5">
            ID: {b.id} {!expanded && <span className="text-[#E83E8C]">- tap for details</span>}
          </p>
        </div>
        <span className={`text-xs px-2.5 py-1 rounded-full font-semibold whitespace-nowrap ${statusClass}`}>
          {statusLabel}
        </span>
      </div>

      {expanded && (
        <div className="mt-3 pt-3 border-t border-[#f0e6d9] space-y-2 text-xs">
          {b.setup_time && <Row label="Setup Time" value={b.setup_time} />}
          {b.address && <Row label="Address" value={b.address} valueClass="text-right max-w-[60%]" />}
          {b.total_amount && <Row label="Total Amount" value={`Rs ${Number(b.total_amount).toLocaleString()}`} />}
          {b.amount_paid && <Row label="Amount Paid" value={`Rs ${Number(b.amount_paid).toLocaleString()}`} valueClass="text-green-700" />}
          {balance !== null && (
            <div className="flex justify-between bg-amber-50 p-2 rounded-lg">
              <span className="text-amber-700 font-medium">Balance Due</span>
              <span className="font-bold text-amber-700">Rs {balance.toLocaleString()}</span>
            </div>
          )}
          {b.add_ons && b.add_ons.length > 0 && (
            <div>
              <span className="text-[#475569]">Add-ons:</span>
              <div className="flex flex-wrap gap-1 mt-1">
                {b.add_ons.map((a, i) => (
                  <span key={a.id || a.name || i} className="px-2 py-0.5 bg-[#E83E8C]/10 text-[#E83E8C] rounded-full text-[10px] font-medium">
                    {typeof a === 'string' ? a : a.name || a.addon_name}
                  </span>
                ))}
              </div>
            </div>
          )}
          {b.payment_type && <Row label="Payment Type" value={b.payment_type} valueClass="capitalize" />}
          {b.notes && (
            <div>
              <span className="text-[#475569]">Notes:</span>
              <p className="text-[#1E1B4B] mt-0.5">{b.notes}</p>
            </div>
          )}
        </div>
      )}

      {isUpcoming && (
        <div className="flex flex-wrap gap-2 mt-3 pt-3 border-t border-[#f0e6d9]">
          <ActionBtn onClick={onAddAddon} icon={Plus} testId={`add-addon-${b.id}`} color="bg-[#FF9F1C]/10 text-[#FF9F1C] hover:bg-[#FF9F1C]/20">Add Add-ons</ActionBtn>
          <ActionBtn onClick={onEditRequest} icon={Calendar} testId={`edit-booking-${b.id}`} color="bg-indigo-50 text-indigo-700 hover:bg-indigo-100">Request Edit</ActionBtn>
          <button onClick={onSmartInvite} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-gradient-to-r from-[#E83E8C] to-[#FF9F1C] text-white text-xs font-semibold hover:opacity-90 transition-all shadow-sm" data-testid={`smart-invite-${b.id}`}>
            <Sparkles size={12} /> AI Smart Invite
          </button>
          <ActionBtn onClick={onTextInvite} icon={Share2} testId={`invite-friends-${b.id}`} color="bg-[#E83E8C]/10 text-[#E83E8C] hover:bg-[#E83E8C]/20">Text Invite</ActionBtn>
          {(b.paid_amount > 0 || b.payment_status === "paid" || b.payment_status === "partial") && (
            <a
              href={`${process.env.REACT_APP_BACKEND_URL}/api/bookings/invoice.pdf?booking_id=${encodeURIComponent(b.id)}&phone=${encodeURIComponent(b.customer_phone || "")}`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#1E1B4B]/5 text-[#1E1B4B] text-xs font-semibold hover:bg-[#1E1B4B]/10 transition"
              data-testid={`download-invoice-${b.id}`}
            >
              <FileText size={12} /> Tax Invoice
            </a>
          )}
          <ActionBtn onClick={onCancel} icon={X} testId={`cancel-booking-${b.id}`} color="bg-gray-100 text-[#475569] hover:bg-gray-200">Cancel</ActionBtn>
        </div>
      )}
    </div>
  );
}

const Row = ({ label, value, valueClass = "" }) => (
  <div className="flex justify-between">
    <span className="text-[#475569]">{label}</span>
    <span className={`font-medium text-[#1E1B4B] ${valueClass}`}>{value}</span>
  </div>
);

const ActionBtn = ({ onClick, icon: Icon, testId, color, children }) => (
  <button onClick={onClick} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg ${color} text-xs font-medium transition-colors`} data-testid={testId}>
    <Icon size={12} /> {children}
  </button>
);
